// src/pages/LoginPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { FiEye, FiEyeOff } from "react-icons/fi";
import "../styles/LoginPage.css";
import { setLoggedIn, setProfile, setToken } from "../utils/auth";
import { useTranslation } from "react-i18next";

export default function LoginPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Register’dan /login?pending=1 ile gelince mesaj göster
  const pending = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("pending") === "1";
  }, [location.search]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");
    setLoading(true);

    const u = username.toLowerCase().trim();

    try {
      // 1) Login (backend token döndürüyor)
      const loginRes = await axiosInstance.post("/api/auth/login/", {
        username: u,
        password,
      });

      // ✅ Backend response: { token: "..." }
      const apiToken = loginRes.data?.token;
      if (!apiToken) {
        throw new Error(t("login.errors.noToken"));
      }

      // 2) Token kaydet
      // setToken fonksiyonun daha önce access/refresh bekliyordu,
      // ama bozmayalım diye 2.parametreyi null geçiyoruz.
      setToken(apiToken, null);
      localStorage.setItem("token", apiToken);
      localStorage.setItem("last_username", u);

      // 3) Profil çek
      const meRes = await axiosInstance.get("/api/auth/me/");

      setProfile({
        role: meRes.data?.role ?? null,
        can_post: !!meRes.data?.can_post,
        can_analyze: !!meRes.data?.can_analyze,
      });

      setLoggedIn(true);
      navigate("/");
    } catch (err) {
        const data = err?.response?.data;
        const status = err?.response?.status;

        if (data?.code === "membership_expired") {
          localStorage.setItem("renewEmail", data.email || "");
          localStorage.setItem("renewUsername", data.username || "");
          window.location.href = "/renew-subscription";
          return;
        }

      console.log("LOGIN FAIL:", {
        status,
        data,
        message: err?.message,
        code: err?.code,
      });

      // Network/CORS/SSL vs: err.response yoksa buraya düşer
      if (!status) {
        setError(`${t("login.errors.cannotConnect")}: ${err?.message || "Network error"}`);
        return;
      }

      // Backend detail mesajı (403/402 dahil) burada
      const detail =
        data?.detail ||
        data?.error ||
        t("login.errors.httpError", { status });

      // 401: yanlış şifre/kullanıcı
      if (status === 401) {
        setError(t("login.errors.wrongCredentials"));
        return;
      }

      // 400: validation/detail mesajları
      if (status === 400) {
        const msg =
          data?.detail ||
          (data?.username ? `${t("login.errors.usernamePrefix")}: ${data.username.join(" ")}` : "") ||
          (data?.email ? `${t("login.errors.emailPrefix")}: ${data.email.join(" ")}` : "") ||
          (data?.password ? `${t("login.errors.passwordPrefix")}: ${data.password.join(" ")}` : "") ||
          t("login.errors.invalidOrMissing");
        setError(msg);
        return;
      }

      // ✅ 403: incelemede / onay yok -> backend mesajını göster
      if (status === 403) {
        setError(detail);
        return;
      }

      // ✅ 402: ödeme gerekiyor -> backend mesajını göster
      if (status === 402) {
        setError(detail);
        return;
      }

      // diğerleri
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

    useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get("renew") === "success") {
      alert(t("login.renewSuccess"));
      window.history.replaceState({}, "", "/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="auth-container ta dental">
      <div className="auth-box ta dental">
        <h2 className="auth-title ta dental">{t("login.title")}</h2>

        {/* ✅ Pending bilgilendirme */}
        {pending && (
          <div
            className="position-alert ta dental"
            style={{
              background: "#eef6ff",
              border: "1px solid #bcdcff",
              color: "#1f3b5c",
            }}
          >
            {t("login.pendingNotice")}
          </div>
        )}

        {error && <div className="position-alert ta dental">{error}</div>}

        <form className="auth-form ta dental" onSubmit={handleSubmit}>
          <label className="ta dental" htmlFor="login-username">
            {t("login.usernameLabel")}
          </label>
          <input
            id="login-username"
            className="ta dental"
            type="text"
            placeholder={t("login.usernamePlaceholder")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />

          <label className="ta dental" htmlFor="login-password">
            {t("login.passwordLabel")}
          </label>

          <div className="password-wrapper ta dental">
            <input
              id="login-password"
              className="ta dental"
              type={showPwd ? "text" : "password"}
              placeholder={t("login.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="toggle-password icon-btn"
              onClick={() => setShowPwd((s) => !s)}
              aria-label={showPwd ? t("login.toggleHide") : t("login.toggleShow")}
              title={showPwd ? t("login.toggleHide") : t("login.toggleShow")}
            >
              {showPwd ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          <button type="submit" className="auth-button ta dental" disabled={loading}>
            {loading ? t("login.submitting") : t("login.submit")}
          </button>
        </form>

        <p className="auth-link ta dental">
          {t("login.noAccount")} <Link to="/register">{t("login.register")}</Link>
        </p>
        <p className="auth-link ta dental">
          <Link to="/forgot-password">{t("login.forgot")}</Link>
        </p>
      </div>
    </div>
  );
}